import React from "react";
import { useUser, useClerk, useReverification } from "@clerk/clerk-react";
import { Avatar } from "../components/Avatar";
import {
  Button,
  Card,
  Field,
  Grid,
  Input,
  Modal,
  Pill,
  SectionHead,
  Table,
} from "../design";
import { fmtDate } from "../lib/format";
import { isAdminRole, roleLabel } from "../lib/roles";
import type { Me, Org } from "../lib/types";
import { clearCachedMe } from "../lib/meCache";
import { clearSignupIntent } from "../lib/intent";
import { landingUrl } from "../lib/urls";

function clerkErr(e: unknown): string {
  const x = e as {
    errors?: Array<{ longMessage?: string; message?: string }>;
    message?: string;
  };
  return (
    x?.errors?.[0]?.longMessage ||
    x?.errors?.[0]?.message ||
    x?.message ||
    "Something went wrong."
  );
}

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// True when the user dismissed Clerk's step-up reverification modal —
// matches isReverificationCancelledError without the subpath import
// (which this tsconfig's "Node" resolution can't see).
function isCancel(e: unknown): boolean {
  return (e as { code?: string } | null)?.code === "reverification_cancelled";
}

function NewPasswordPair({
  next,
  setNext,
  confirm,
  setConfirm,
}: {
  next: string;
  setNext: (v: string) => void;
  confirm: string;
  setConfirm: (v: string) => void;
}) {
  const strength = Math.min(4, Math.floor(next.length / 4));
  const strengthLabels = ["Weak", "Okay", "Good", "Strong", "Excellent"];
  const level = strength >= 3 ? "ok" : strength >= 2 ? "mid" : "weak";
  const mismatch = !!confirm && confirm !== next;
  return (
    <>
      <Field
        label="New password"
        hint={
          next ? (
            <span className={`acct-pw ${level}`}>
              <span className="acct-pw-bar">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={i < strength ? "on" : undefined} />
                ))}
              </span>
              {strengthLabels[strength]}
            </span>
          ) : undefined
        }
      >
        <Input
          type="password"
          block
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="At least 8 characters"
          autoComplete="new-password"
        />
      </Field>
      <Field
        label="Confirm new password"
        error={mismatch ? "Passwords don't match" : null}
      >
        <Input
          type="password"
          block
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
      </Field>
    </>
  );
}

export function PasswordSection() {
  const { user } = useUser();
  const isChange = !!user?.passwordEnabled;
  // Clerk step-up reverification collects identity proof in its own modal,
  // so there is no inline "current password" field.
  const update = useReverification(
    (p: { newPassword: string; signOutOfOtherSessions?: boolean }) =>
      user!.updatePassword(p),
  );
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [err, setErr] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const canSubmit = !!next && next === confirm && !saving;

  const hint = err
    ? err
    : done
      ? "Password updated. Other sessions signed out."
      : !next
        ? "Choose a new password (min 8 characters)."
        : "Saving signs out your other sessions.";

  async function submit() {
    if (!user || !canSubmit) return;
    setErr("");
    setDone(false);
    setSaving(true);
    try {
      await update({ newPassword: next, signOutOfOtherSessions: true });
      setDone(true);
      setNext("");
      setConfirm("");
    } catch (e) {
      if (isCancel(e)) return;
      setErr(clerkErr(e));
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  return (
    <div className="acct">
      <SectionHead
        title={isChange ? "Change password" : "Set a password"}
        info={
          isChange
            ? undefined
            : "You sign in with a connected account. Add a password to sign in with email and password too."
        }
      />
      <Card>
        <form
          className="acct-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Grid cols={2}>
            <NewPasswordPair
              next={next}
              setNext={setNext}
              confirm={confirm}
              setConfirm={setConfirm}
            />
          </Grid>
          <div className="acct-foot">
            <span
              className={`acct-note${err ? " err" : done ? " ok" : ""}`}
              role={err || done ? "status" : undefined}
            >
              {hint}
            </span>
            <Button
              variant="primary"
              type="submit"
              disabled={!canSubmit}
              busy={saving}
            >
              {saving ? "Saving…" : "Save password"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function DeleteModal({ onClose }: { onClose: () => void }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const deleteAccount = useReverification(() => user!.delete());
  const [confirm, setConfirm] = React.useState("");
  const [err, setErr] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  async function del() {
    if (!user || confirm !== "DELETE") return;
    setErr("");
    setBusy(true);
    try {
      await deleteAccount();
      clearCachedMe();
      clearSignupIntent();
      await signOut({ redirectUrl: landingUrl() });
    } catch (e) {
      if (isCancel(e)) {
        setBusy(false);
        return;
      }
      setErr(clerkErr(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Delete account"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            icon="trash"
            disabled={confirm !== "DELETE"}
            busy={busy}
            onClick={del}
          >
            {busy ? "Deleting…" : "Delete account"}
          </Button>
        </>
      }
    >
      <Field
        label="Type DELETE to confirm"
        hint="Deletes your account, keys, logs and settings. Required billing records are retained. It can't be undone."
        error={err || null}
      >
        <Input
          type="text"
          block
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="DELETE"
        />
      </Field>
    </Modal>
  );
}

export function Account({
  me,
  org,
  hideHeader,
}: {
  me: Me;
  org?: Org | null;
  hideHeader?: boolean;
}) {
  const { user } = useUser();
  const [delOpen, setDelOpen] = React.useState(false);

  const [first, setFirst] = React.useState("");
  const [last, setLast] = React.useState("");
  const [nameSaving, setNameSaving] = React.useState(false);
  const [nameErr, setNameErr] = React.useState("");
  React.useEffect(() => {
    setFirst(user?.firstName || "");
    setLast(user?.lastName || "");
  }, [user?.firstName, user?.lastName]);

  async function saveName() {
    if (!user) return;
    setNameSaving(true);
    setNameErr("");
    try {
      await user.update({ firstName: first, lastName: last });
    } catch (e) {
      setNameErr(clerkErr(e));
    } finally {
      setNameSaving(false);
    }
  }

  if (!user) return null;

  const email = user.primaryEmailAddress?.emailAddress || me.email || "—";
  const nameDirty =
    first !== (user.firstName || "") || last !== (user.lastName || "");
  const methods: Array<{ label: string; id: string }> = [
    ...user.externalAccounts.map((a) => ({
      label: cap(a.provider.replace(/^oauth_/, "")),
      id: a.emailAddress || "Connected",
    })),
    ...(user.passwordEnabled ? [{ label: "Password", id: "••••••••" }] : []),
  ];

  return (
    <div className="acct">
      {!hideHeader && <SectionHead title="Profile" />}

      <Card title="Profile" info="Used on your invoices and emails.">
        <div className="acct-profile">
          {user.imageUrl ? (
            <img
              src={user.imageUrl}
              alt=""
              width={56}
              height={56}
              className="acct-avatar"
            />
          ) : (
            <Avatar email={email} name={user.fullName || ""} size="lg" status />
          )}
          <div className="acct-form">
            <Grid cols={2}>
              <Field label="First name">
                <Input
                  block
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                />
              </Field>
              <Field label="Last name">
                <Input
                  block
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                />
              </Field>
              <Field label="Email">
                <Input block value={email} readOnly />
              </Field>
              <Field label="Joined">
                <Input
                  block
                  value={fmtDate(
                    user.createdAt ? user.createdAt.toISOString() : null,
                  )}
                  readOnly
                />
              </Field>
            </Grid>
            <div className="acct-foot">
              <span
                className={`acct-note${nameErr ? " err" : ""}`}
                role={nameErr ? "alert" : undefined}
              >
                {nameErr}
              </span>
              <Button
                variant="primary"
                disabled={!nameDirty}
                busy={nameSaving}
                onClick={saveName}
              >
                {nameSaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card flush title="Sign-in methods">
        <Table label="Sign-in methods">
          <thead>
            <tr>
              <th>Method</th>
              <th>Identifier</th>
            </tr>
          </thead>
          <tbody>
            {methods.length === 0 ? (
              <tr>
                <td className="muted" colSpan={2}>
                  No sign-in methods
                </td>
              </tr>
            ) : (
              methods.map((m, i) => (
                <tr key={i}>
                  <td className="strong">{m.label}</td>
                  <td className="muted">{m.id}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {org && (
        <Card title="Organization">
          <div className="acct-org">
            <div>
              <div className="acct-org-name">
                {org.name || org.slug || "Organization"}
              </div>
              {org.slug && org.name && (
                <div className="acct-org-slug">{org.slug}</div>
              )}
            </div>
            <Pill tone={isAdminRole(org.role) ? "info" : "neutral"}>
              {cap(roleLabel(org.role))}
            </Pill>
          </div>
        </Card>
      )}

      <Card className="acct-danger">
        <div className="acct-org">
          <div>
            <div className="acct-org-name">Delete account</div>
            <div className="acct-note">
              Removes your account, keys, logs and settings. Required billing
              records are retained.
            </div>
          </div>
          <Button
            variant="danger"
            icon="trash"
            onClick={() => setDelOpen(true)}
          >
            Delete account
          </Button>
        </div>
      </Card>

      {delOpen && <DeleteModal onClose={() => setDelOpen(false)} />}
    </div>
  );
}
